import React, { useState, useEffect, useMemo, useRef, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { db } from '../db';
import '../App.css';
import * as XLSX from 'xlsx';

const API_URL = 'https://keywords-tool-api.maksymafendi.workers.dev';

interface BasicList {
    name: string;
    keywords: string[];
}

const EmptyPathIcon = ({ children, ...props }: any) => (
    <svg xmlns="http://www.w3.org/2000/svg" height="24" viewBox="0 0 24 24" width="24" fill="currentColor" {...props}>
       {children}
    </svg>
);

const ChevronUpIcon = () => <EmptyPathIcon><path d="M12 8l-6 6 1.41 1.41L12 10.83l4.59 4.58L18 14l-6-6z"/></EmptyPathIcon>;
const ChevronDownIcon = () => <EmptyPathIcon><path d="M16.59 8.59L12 13.17 7.41 8.59 6 10l6 6 6-6-1.41-1.41z"/></EmptyPathIcon>;
const SearchIcon = () => <EmptyPathIcon><path d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/></EmptyPathIcon>;
const EyeIcon = () => <EmptyPathIcon><path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5C21.27 7.61 17 4.5 12 4.5zm0 12c-2.48 0-4.5-2.02-4.5-4.5S9.52 7.5 12 7.5s4.5 2.02 4.5 4.5-2.02 4.5-4.5 4.5zm0-7a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5z"/></EmptyPathIcon>;
const CopyIcon = () => <EmptyPathIcon><path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z"/></EmptyPathIcon>;
const TrashIcon = () => <EmptyPathIcon><path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/></EmptyPathIcon>;
const AddIcon = () => <EmptyPathIcon><path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/></EmptyPathIcon>;
const EditIcon = () => <EmptyPathIcon><path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/></EmptyPathIcon>;
const DownloadIcon = () => <EmptyPathIcon><path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z"/></EmptyPathIcon>;
const CloseIcon = () => <EmptyPathIcon><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/></EmptyPathIcon>;

function DashboardPage() {
    // --- Хуки ---
    const auth = useContext(AuthContext);
    const navigate = useNavigate();
    const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const sourcePhrasesDebounceRef = useRef<NodeJS.Timeout | null>(null);
    const downloadMenuRef = useRef<HTMLDivElement>(null);
    const matchesModalRef = useRef<HTMLDivElement>(null);
    const listSelectionModalRef = useRef<HTMLDivElement>(null);

    // --- Состояния ---
    const [sourcePhrases, setSourcePhrases] = useState<string>('');
    const [isSourcePhrasesVisible, setIsSourcePhrasesVisible] = useState<boolean>(true);
    const [uniqueWords, setUniqueWords] = useState<string[]>([]);
    const [selectedUniqueWords, setSelectedUniqueWords] = useState<Set<string>>(new Set());
    const [selectAllUniqueWords, setSelectAllUniqueWords] = useState<boolean>(false);
    const [uniqueWordsSearchQuery, setUniqueWordsSearchQuery] = useState<string>('');
    const [isSearchVisible, setIsSearchVisible] = useState(false);
    const [basicLists, setBasicLists] = useState<BasicList[]>([]);
    const [activeBasicListName, setActiveBasicListName] = useState<string>('');
    const [newBasicListName, setNewBasicListName] = useState<string>('');
    const [basicListSearchQuery, setBasicListSearchQuery] = useState<string>('');
    const [message, setMessage] = useState<string>('');
    const [highlightMatchesModal, setHighlightMatchesModal] = useState<boolean>(false);
    const [matchesList, setMatchesList] = useState<string[]>([]);
    const [wordToHighlight, setWordToHighlight] = useState<string>('');
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');

    const [showNewListInput, setShowNewListInput] = useState(false);
    const [showListSelectionModal, setShowListSelectionModal] = useState(false);
    const [isBasicKeywordsVisible, setIsBasicKeywordsVisible] = useState(false);
    const [editingListName, setEditingListName] = useState<string | null>(null);
    const [newListName, setNewListName] = useState('');
    const [isDownloadMenuVisible, setDownloadMenuVisible] = useState(false);
    

    const filteredBasicLists = useMemo(() => {
        const query = basicListSearchQuery.toLowerCase().trim();
        if (!query) return basicLists;
        return basicLists.filter(list => list.name.toLowerCase().includes(query));
    }, [basicLists, basicListSearchQuery]);

    const filteredUniqueWords = useMemo(() => {
        const query = uniqueWordsSearchQuery.toLowerCase().trim();
        if (!query) return uniqueWords;
        return uniqueWords.filter(word => word.toLowerCase().includes(query));
    }, [uniqueWords, uniqueWordsSearchQuery]);

    const actionableSelectedWords = useMemo(() => {
        const filteredSet = new Set(filteredUniqueWords);
        return Array.from(selectedUniqueWords).filter(word => filteredSet.has(word));
    }, [selectedUniqueWords, filteredUniqueWords]);

    const sourcePhrasesCount = useMemo(() => {
        return sourcePhrases.split('\n').filter(line => line.trim() !== '').length;
    }, [sourcePhrases]);
    
    const activeKeywordsCount = useMemo(() => {
        const activeList = basicLists.find(list => list.name === activeBasicListName);
        if (!activeList || !activeList.keywords) return 0;
        return activeList.keywords.filter(kw => kw.trim() !== '').length;
    }, [basicLists, activeBasicListName]);

    // --- Эффекты ---
    useEffect(() => {
        if (!auth?.token) { navigate('/login'); return; }
        const loadLists = async () => {
            setIsLoading(true);
            try {
                const response = await fetch(`${API_URL}/api/lists`, { headers: { 'Authorization': `Bearer ${auth.token}` } });
                if (!response.ok) throw new Error('Не удалось загрузить списки.');
                const data: BasicList[] = await response.json();
                if (data && data.length > 0) {
                    setBasicLists(data);
                    const storedActive = localStorage.getItem(`activeBasicListName_${auth.user?.username}`);
                    if (storedActive && data.some(l => l.name === storedActive)) setActiveBasicListName(storedActive);
                    else setActiveBasicListName(data[0].name);
                } else {
                    const defaultList = [{ name: 'Default', keywords: [] }];
                    setBasicLists(defaultList);
                    setActiveBasicListName('Default');
                }
            } catch (err: any) { setError(err.message); }
            finally { setIsLoading(false); }
        };
        loadLists();
    }, [auth, navigate]);

    useEffect(() => {
        const loadSessionData = async () => {
            const savedPhrases = await db.sessionData.get('sourcePhrases');
            const savedWords = await db.sessionData.get('uniqueWords');
            if (savedPhrases) setSourcePhrases(savedPhrases.value);
            if (savedWords) setUniqueWords(savedWords.value);
        };
        loadSessionData();
    }, []);

    useEffect(() => {
        if (sourcePhrasesDebounceRef.current) clearTimeout(sourcePhrasesDebounceRef.current);
        sourcePhrasesDebounceRef.current = setTimeout(() => {
            db.sessionData.put({ key: 'sourcePhrases', value: sourcePhrases });
        }, 500);
    }, [sourcePhrases]);

    useEffect(() => {
        if (isLoading) return;
        if (debounceTimeoutRef.current) clearTimeout(debounceTimeoutRef.current);
        debounceTimeoutRef.current = setTimeout(async () => {
            if (!auth?.token || basicLists.length === 0) return;
            showSnackbar("Сохранение на сервере...");
            localStorage.setItem(`activeBasicListName_${auth?.user?.username}`, activeBasicListName);
            try {
                await fetch(`${API_URL}/api/lists`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${auth.token}` },
                    body: JSON.stringify(basicLists)
                });
                showSnackbar("Списки успешно сохранены на сервере.");
            } catch (err) { showSnackbar('Ошибка сохранения на сервере.'); }
        }, 2000);
        return () => { if (debounceTimeoutRef.current) clearTimeout(debounceTimeoutRef.current); };
    }, [basicLists, activeBasicListName, auth, isLoading]);

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                if (highlightMatchesModal) setHighlightMatchesModal(false);
                if (isSearchVisible) closeAndClearSearch();
                if (showListSelectionModal) setShowListSelectionModal(false);
                if (showNewListInput) setShowNewListInput(false);
                if (editingListName) setEditingListName(null);
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [highlightMatchesModal, isSearchVisible, showListSelectionModal, showNewListInput, editingListName]);
    
    useEffect(() => {
        if (filteredUniqueWords.length === 0) {
            setSelectAllUniqueWords(false);
            return;
        }
        const allVisibleSelected = filteredUniqueWords.every(word => selectedUniqueWords.has(word));
        setSelectAllUniqueWords(allVisibleSelected);
    }, [selectedUniqueWords, filteredUniqueWords]);
    
    
   
    useEffect(() => {
        // Функция, которая будет вызываться при клике на странице
        const handleClickOutside = (event: MouseEvent) => {
            // Проверяем, что "якорь" существует и клик был НЕ внутри элемента с этим "якорем"
            if (downloadMenuRef.current && !downloadMenuRef.current.contains(event.target as Node)) {
                setDownloadMenuVisible(false); // Закрываем меню
            }
        };

        // Добавляем слушатель кликов на всю страницу, ТОЛЬКО ЕСЛИ меню открыто
        if (isDownloadMenuVisible) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        // Важно: убираем слушатель, когда компонент исчезает или меню закрывается
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isDownloadMenuVisible]); // Этот эффект будет срабатывать при каждом изменении видимости меню
    


    // --- Эффект для закрытия модального окна совпадений по клику снаружи ---
useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
        if (matchesModalRef.current && !matchesModalRef.current.contains(event.target as Node)) {
            setHighlightMatchesModal(false);
        }
    };

    if (highlightMatchesModal) {
        document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
        document.removeEventListener('mousedown', handleClickOutside);
    };
}, [highlightMatchesModal]);
    
    // --- Эффект для закрытия модального окна совпадений по клику снаружи ---
    useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
        if (listSelectionModalRef.current && !listSelectionModalRef.current.contains(event.target as Node)) {
            setShowListSelectionModal(false);
        }
    };

    if (showListSelectionModal) {
        document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
        document.removeEventListener('mousedown', handleClickOutside);
    };
}, [showListSelectionModal]);
    
    
    
    
    
    
    
// Улучшенный обработчик для предотвращения зума на всех полях ввода
useEffect(() => {
    // Находим мета-тег viewport, который управляет масштабом
    const viewport = document.querySelector('meta[name="viewport"]');
    if (!viewport) return; // Если тега нет, ничего не делаем

    // Сохраняем исходное значение, чтобы вернуть его позже
    const originalContent = viewport.getAttribute('content');
    
    // CSS-селектор, который описывает все наши проблемные поля ввода
    const targetSelector = '.new-list-name-input, .basic-list-search-input, .search-input-field';

    // ИСПРАВЛЕНИЕ: Добавляем тип 'FocusEvent' для параметра event
    const handleFocus = (event: FocusEvent) => {
        // Проверяем, является ли цель события элементом и соответствует ли селектору
        if (event.target instanceof Element && event.target.matches(targetSelector)) {
            viewport.setAttribute('content', 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no');
        }
    };

    // ИСПРАВЛЕНИЕ: Добавляем тип 'FocusEvent' и здесь
    const handleBlur = (event: FocusEvent) => {
        // Проверяем, уходит ли фокус с одного из наших целевых элементов
        if (event.target instanceof Element && event.target.matches(targetSelector)) {
            // Возвращаем исходные настройки, разрешая масштабирование
            if (originalContent) {
                viewport.setAttribute('content', originalContent);
            }
        }
    };

    // Добавляем обработчики событий на весь документ.
    document.addEventListener('focus', handleFocus, true);
    document.addEventListener('blur', handleBlur, true);

    // Убираем обработчики при размонтировании компонента
    return () => {
        document.removeEventListener('focus', handleFocus, true);
        document.removeEventListener('blur', handleBlur, true);
    };
}, []); // Пустой массив зависимостей гарантирует, что этот код выполнится только один раз
    
    
    
    
    
    
    if (!auth || !auth.user) return null;
    const { logout, user } = auth;

    // --- Функции ---
    const showSnackbar = (msg: string) => { setMessage(msg); setTimeout(() => setMessage(''), 3000); };
    const getActiveBasicKeywords = (): string[] => basicLists.find(list => list.name === activeBasicListName)?.keywords || [];
    
    const updateActiveBasicKeywords = (newKeywords: string[]) => {
        setBasicLists(prevLists =>
            prevLists.map(list => list.name === activeBasicListName ? { ...list, keywords: newKeywords } : list)
        );
    };

    const closeAndClearSearch = () => {
        setUniqueWordsSearchQuery('');
        setIsSearchVisible(false);
    };

    const handleToggleSearch = () => setIsSearchVisible(prev => !prev);
    
    const handleSplitWords = async () => {
        const phrases = sourcePhrases.split('\n').filter(line => line.trim() !== '');
        if (phrases.length === 0) { showSnackbar('Введите исходные фразы.'); return; }
        let allWords: string[] = [];
        phrases.forEach(phrase => {
            String(phrase).split(/[\s,.;:!?«»()"'`~@#$%^&*+=_/\\|-]+/g).forEach(w => {
                w = w.trim().toLowerCase();
                if (w) allWords.push(w);
            });
        });
        const newUniqWords = Array.from(new Set(allWords)).sort();
        setUniqueWords(newUniqWords);
        setSelectedUniqueWords(new Set());
        await db.sessionData.put({ key: 'uniqueWords', value: newUniqWords });
        showSnackbar(`Уникальных слов: ${newUniqWords.length}`);
        setIsSourcePhrasesVisible(false);
    };

    const handleUniqueWordCheckboxChange = (word: string, isChecked: boolean) => {
        setSelectedUniqueWords(prev => {
           const newSet = new Set(prev);
           isChecked ? newSet.add(word) : newSet.delete(word);
           return newSet;
       });
    };

    const handleSelectAllUniqueWordsChange = (isChecked: boolean) => {
       setSelectedUniqueWords(prevSelected => {
           const newSelected = new Set(prevSelected);
           if (isChecked) {
               filteredUniqueWords.forEach(word => newSelected.add(word));
           } else {
               filteredUniqueWords.forEach(word => newSelected.delete(word));
           }
           return newSelected;
       });
    };

    const findAndShowMatchesForWord = (wordToSearch: string) => {
       const word = wordToSearch.trim().toLowerCase();
       if (!word) { showSnackbar('Слово для поиска не может быть пустым.'); return; }
       const phrases = sourcePhrases.split('\n').filter(line => line.trim() !== '');
       if (phrases.length === 0) {
           showSnackbar('Нет фраз для поиска в источнике.');
           setMatchesList([]);
           setWordToHighlight(wordToSearch);
           setHighlightMatchesModal(true);
           return;
       }
       const safeWord = word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
       const re = new RegExp(`(^|[^\\p{L}\\p{N}])${safeWord}($|[^\\p{L}\\p{N}])`, 'iu');
       const foundMatches: string[] = phrases.filter(phrase => re.test(phrase));
       setMatchesList(foundMatches);
       setWordToHighlight(wordToSearch);
       setHighlightMatchesModal(true);
       showSnackbar(`Найдено совпадений: ${foundMatches.length} для "${wordToSearch}"`);
    };

    const findPhrasesForSelectedWords = () => {
       const selectedWords = actionableSelectedWords;
       if (selectedWords.length === 0) {
           showSnackbar('Отметьте слова для поиска в видимой области.');
           return;
       }
       const phrases = sourcePhrases.split('\n').filter(line => line.trim() !== '');
       if (phrases.length === 0) {
           showSnackbar('Нет фраз для поиска в источнике.');
           return;
       }
       const safeWords = selectedWords.map(word => word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
       const regex = new RegExp(`(^|[^\\p{L}\\p{N}])(${safeWords.join('|')})($|[^\\p{L}\\p{N}])`, 'iu');
       const foundMatches = phrases.filter(phrase => regex.test(phrase));
       setMatchesList(foundMatches);
       setWordToHighlight(`выбранных слов (${selectedWords.length})`);
       setHighlightMatchesModal(true);
       showSnackbar(`Найдено фраз: ${foundMatches.length}`);
    };

    const handleCopySelectedWords = async () => {
       const wordsToCopyArr = actionableSelectedWords;
       if (wordsToCopyArr.length === 0) { showSnackbar('Не выбрано ни одного видимого слова для копирования.'); return; }
       const wordsToCopy = wordsToCopyArr.join('\n');
       try {
           await navigator.clipboard.writeText(wordsToCopy);
           showSnackbar(`Скопировано ${wordsToCopyArr.length} слов.`);
       } catch (err) {
           showSnackbar('Ошибка копирования.');
       }
    };

    const handleDeleteSelectedUniqueWords = async () => {
        const wordsToDelete = new Set(actionableSelectedWords);
        const countToDelete = wordsToDelete.size;
        if (countToDelete === 0) {
            showSnackbar('Не выбрано ни одного видимого слова для удаления.');
            return;
        }
        const newWords = uniqueWords.filter(word => !wordsToDelete.has(word));
        setUniqueWords(newWords);

        const newSelected = new Set(selectedUniqueWords);
        wordsToDelete.forEach(word => newSelected.delete(word));
        setSelectedUniqueWords(newSelected);

        await db.sessionData.put({ key: 'uniqueWords', value: newWords });
        showSnackbar(`Удалено слов: ${countToDelete}`);
    };

    const handleMoveWordsToBasic = async () => {
        const wordsToMove = actionableSelectedWords;
        if (wordsToMove.length === 0 || !activeBasicListName) { showSnackbar('Выберите видимые слова или активный список.'); return; }

        const activeKeywords = getActiveBasicKeywords();
        const newKeywords = Array.from(new Set([...activeKeywords, ...wordsToMove])).sort();
        updateActiveBasicKeywords(newKeywords);

        const wordsToMoveSet = new Set(wordsToMove);
        const newUniqueWords = uniqueWords.filter(word => !wordsToMoveSet.has(word));
        setUniqueWords(newUniqueWords);

        const newSelected = new Set(selectedUniqueWords);
        wordsToMoveSet.forEach(word => newSelected.delete(word));
        setSelectedUniqueWords(newSelected);

        await db.sessionData.put({ key: 'uniqueWords', value: newUniqueWords });
        showSnackbar(`Добавлено в Basic: ${wordsToMove.length} слов.`);
    };

    const handleCopyMatchesList = async () => {
       if (matchesList.length === 0) {
           showSnackbar('Нет фраз для копирования.');
           return;
       }
       
    
       const textToCopy = matchesList.join('\n');
       try {
           await navigator.clipboard.writeText(textToCopy);
           showSnackbar(`Скопировано ${matchesList.length} фраз.`);
       } catch (err) {
           showSnackbar('Ошибка копирования.');
       }
    };

    
    
    // удаление фраз из попап
    
        const handleRemoveMatch = (indexToRemove: number) => {
        setMatchesList(prevMatches => prevMatches.filter((_, index) => index !== indexToRemove));
    };

    
    
    
    
    const handlePurgeByBasic = async () => {
        const basicWordsSet = new Set(getActiveBasicKeywords().map(w => w.toLowerCase()));
        if (basicWordsSet.size === 0) { showSnackbar('Нет активных базовых слов для очистки.'); return; }
        const newUniqueWords = uniqueWords.filter(word => !basicWordsSet.has(word.toLowerCase()));
        const countDeleted = uniqueWords.length - newUniqueWords.length;
        setUniqueWords(newUniqueWords);
        setSelectedUniqueWords(new Set());
        await db.sessionData.put({ key: 'uniqueWords', value: newUniqueWords });
        showSnackbar(`Удалено: ${countDeleted} слов.`);
    };

    const handleCreateNewList = () => {
       const trimmedName = newBasicListName.trim();
       if (!trimmedName || basicLists.some(list => list.name.toLowerCase() === trimmedName.toLowerCase())) {
           showSnackbar('Список с таким именем уже существует или имя пустое.'); return;
       }
       const newList: BasicList = { name: trimmedName, keywords: [] };
       setBasicLists(prevLists => [...prevLists, newList]);
       setActiveBasicListName(trimmedName);
       setNewBasicListName('');
       setShowNewListInput(false);
    };
    
    const handleDeleteActiveList = () => {
       if (!activeBasicListName || basicLists.length <= 1) { showSnackbar('Невозможно удалить последний список.'); return; }
       if (window.confirm(`Вы уверены, что хотите удалить список "${activeBasicListName}"?`)) {
           const updatedLists = basicLists.filter(list => list.name !== activeBasicListName);
           setBasicLists(updatedLists);
           setActiveBasicListName(updatedLists[0]?.name || '');
       }
    };
    
    const handleRenameList = async () => {
        const oldName = editingListName;
        const trimmedNewName = newListName.trim();

        if (!oldName || !trimmedNewName || oldName === trimmedNewName) {
            setEditingListName(null);
            setNewListName('');
            return;
        }

        const originalLists = [...basicLists];
        setBasicLists(prevLists => 
            prevLists.map(list => list.name === oldName ? { ...list, name: trimmedNewName } : list)
        );
        if (activeBasicListName === oldName) {
            setActiveBasicListName(trimmedNewName);
        }
        setEditingListName(null);
        setNewListName('');

        try {
            const response = await fetch(`${API_URL}/api/lists/rename`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${auth.token}` },
                body: JSON.stringify({ oldName, newName: trimmedNewName })
            });
            
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Ошибка при переименовании');
            }
            
            showSnackbar(`Список "${oldName}" переименован в "${trimmedNewName}".`);

        } catch (err: any) {
            showSnackbar(`Ошибка: ${err.message}`);
            setBasicLists(originalLists);
            if (activeBasicListName === trimmedNewName) {
                setActiveBasicListName(oldName);
            }
        }
    };
    
    
    const handleSelectSearchedList = (listName: string) => {
       setActiveBasicListName(listName);
       setBasicListSearchQuery('');
       setShowListSelectionModal(false); 
    };
    
    
    // 1. Копирование содержимого
const handleCopyKeywords = async () => {
    const keywordsToCopy = getActiveBasicKeywords();
    if (keywordsToCopy.length === 0) {
        showSnackbar('В активном списке нет слов для копирования.');
        return;
    }
    try {
        await navigator.clipboard.writeText(keywordsToCopy.join('\n'));
        showSnackbar(`Скопировано ${keywordsToCopy.length} слов.`);
    } catch (err) {
        showSnackbar('Ошибка копирования.');
    }
};
    
    
    // Функция для скачивания в формате CSV
    const handleDownloadCSV = () => {
    const keywords = getActiveBasicKeywords();
    if (keywords.length === 0) {
        showSnackbar('В списке нет слов для экспорта.');
        return;
    }

    const BOM = "\uFEFF";
    const csvString = ["Keywords", ...keywords].join("\n");

    const blob = new Blob([BOM + csvString], { type: 'text/csv;charset=utf-8;' });

    const url = URL.createObjectURL(blob);
    
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `${activeBasicListName}.csv`);
    document.body.appendChild(link);

    link.click();

    document.body.removeChild(link);
    URL.revokeObjectURL(url);
};

    // Функция для скачивания в формате XLSX
    const handleDownloadXLSX = () => {
        const keywords = getActiveBasicKeywords();
        if (keywords.length === 0) {
            showSnackbar('В списке нет слов для экспорта.');
            return;
        }
        const worksheetData = keywords.map(kw => ({ "Keyword": kw }));
        const worksheet = XLSX.utils.json_to_sheet(worksheetData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Keywords");
        XLSX.writeFile(workbook, `${activeBasicListName}.xlsx`);
    };
    
    

    // --- JSX ---
    return (
        <div className="container">
            <div className="dashboard-header-controls">
                <button className="btn-small" onClick={logout}>Выйти ({user.username})</button>
                {user.role === 'admin' && (<button className="btn-small" onClick={() => navigate('/admin')}>Админка</button>)}
            </div>
            
            
            {/* Сам список слов <h1>Keywords Tool</h1> */}

            {isSourcePhrasesVisible ? (
                <div className="input-group">
                    <div className="source-phrases-header">
                        <label htmlFor="sourcePhrases">Исходные фразы:</label>
                        <button className="toggle-visibility-btn" onClick={() => setIsSourcePhrasesVisible(false)} title="Свернуть"><ChevronUpIcon /></button>
                    </div>
                    <textarea id="sourcePhrases" value={sourcePhrases} onChange={(e) => setSourcePhrases(e.target.value)} rows={8} placeholder="Введите фразы, каждую с новой строки..."></textarea>
                    <button className="btn btn-half" onClick={handleSplitWords} style={{ marginTop: '8px' }}>Разбить фразы</button>
                </div>
            ) : (
                <div className="collapsed-source-phrases" onClick={() => setIsSourcePhrasesVisible(true)} title="Развернуть">
                    <span>Исходные фразы: <strong>{sourcePhrasesCount}</strong></span>
                    <ChevronDownIcon />
                </div>
            )}
            
            <div className="input-group">
                 <div className="unique-words-header">
                    <label>Уникальные слова:</label>
                    <div className="unique-words-actions">
                        <button className="btn-icon-small" title="Поиск" onClick={handleToggleSearch} disabled={uniqueWords.length === 0}><SearchIcon/></button>
                        <button className="btn-icon-small" title="Показать фразы с выбранными словами" onClick={findPhrasesForSelectedWords} disabled={actionableSelectedWords.length === 0}><EyeIcon /></button>
                        <button className="btn-icon-small" title="Копировать выбранные слова" onClick={handleCopySelectedWords} disabled={actionableSelectedWords.length === 0}><CopyIcon /></button>
                        <button className="btn-icon-small" title="Удалить выбранные слова" onClick={handleDeleteSelectedUniqueWords} disabled={actionableSelectedWords.length === 0}><TrashIcon /></button>
                    </div>
                </div>
                
                {isSearchVisible && ( <div className="search-input-container"> <button className="search-input-icon" onClick={closeAndClearSearch} title="Закрыть поиск"><SearchIcon /></button> <input type="text" placeholder="Поиск по уникальным словам..." className="search-input-field" value={uniqueWordsSearchQuery} onChange={(e) => setUniqueWordsSearchQuery(e.target.value)} autoFocus /> {uniqueWordsSearchQuery && ( <button onClick={() => setUniqueWordsSearchQuery('')} title="Очистить" className="search-input-clear-btn">&times;</button>)}</div>)}
                
                {uniqueWords.length > 0 && ( <> <div className="unique-words-list-container"> <div className="select-all-checkbox-container"> <input type="checkbox" id="selectAllUniqueWords" checked={selectAllUniqueWords} onChange={(e) => handleSelectAllUniqueWordsChange(e.target.checked)} /> <label htmlFor="selectAllUniqueWords">Выбрать все</label> </div> <ul className="unique-words-list"> {filteredUniqueWords.map((word, index) => ( <li key={index} className="unique-word-item"> <input type="checkbox" id={`word-${index}`} checked={selectedUniqueWords.has(word)} onChange={(e) => handleUniqueWordCheckboxChange(word, e.target.checked)} /> <label htmlFor={`word-${index}`}>{word}</label> <button className="btn-icon-small" title={`Найти совпадения для "${word}"`} onClick={() => findAndShowMatchesForWord(word)}><EyeIcon/></button> </li> ))} </ul> </div> </> )}
            </div>

            <div className="action-buttons-row">
                
                <button className="btn" onClick={handleMoveWordsToBasic}>Переместить в список</button>
                <button className="btn" onClick={handlePurgeByBasic}>Очистить по Basic</button>
            </div>
            
            <div className="input-group">
                <div className="basic-list-header">
                    <label onClick={() => setShowListSelectionModal(true)} style={{cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px'}}>
                        Список: 
                        {activeBasicListName && <span className="current-active-list">{activeBasicListName}</span>}
                    </label>
                    <div className="basic-list-header-actions">
                        <button className="btn-icon-small" title="Найти/Выбрать список" onClick={() => setShowListSelectionModal(true)}>
                            <SearchIcon/>
                        </button>
                        <button className="btn-icon-small" title="Создать новый список" onClick={() => {
                             setShowNewListInput(p => !p); // Переключаем состояние
                                  setEditingListName(null);     // Сбрасываем редактирование, если оно было активно
                                       }}>
                                  <AddIcon/>
                        </button>
                        <button 
                            className="btn-icon-small" 
                             title="Переименовать текущий список" 
                               onClick={() => {
                                // Если форма редактирования уже открыта для этого списка, закрываем ее
                                if (editingListName === activeBasicListName) {
                                   setEditingListName(null);
                                  } else {
                                    // Иначе, открываем форму для редактирования
                                    setEditingListName(activeBasicListName);
                                 setNewListName(activeBasicListName);
                                  setShowNewListInput(false); // Сбрасываем создание нового списка
                                      }
                                        }}
                                  disabled={!activeBasicListName}
                                        >
                             <EditIcon/>
                        </button>
                        <button className="btn-icon-small" title="Удалить текущий список" onClick={handleDeleteActiveList} disabled={basicLists.length <= 1}>
                            <TrashIcon/>
                        </button>
                        <button className="toggle-visibility-btn" onClick={() => setIsBasicKeywordsVisible(p => !p)} title={isBasicKeywordsVisible ? "Свернуть" : "Развернуть"}>
                            {isBasicKeywordsVisible ? <ChevronUpIcon/> : <ChevronDownIcon/>}
                        </button>
                    </div>
                </div>

    {/* Блок #1: Формы редактирования и создания. Отображаются над списком. */}
    {editingListName ? (
        <div className="new-list-input-container">
            <input 
                type="text" 
                value={newListName} 
                onChange={(e) => setNewListName(e.target.value)} 
                placeholder="Новое имя списка..." 
                className="new-list-name-input" 
                autoFocus 
                onKeyDown={(e) => e.key === 'Enter' && handleRenameList()}
            />
            <button className="btn-small" onClick={handleRenameList}>Сохранить</button>
        </div>
    ) : showNewListInput ? (
        <div className="new-list-input-container">
            <input 
                type="text" 
                value={newBasicListName} 
                onChange={(e) => setNewBasicListName(e.target.value)} 
                placeholder="Имя нового списка..." 
                className="new-list-name-input" 
                autoFocus 
                onKeyDown={(e) => e.key === 'Enter' && handleCreateNewList()}
            />
            <button className="btn-small" onClick={handleCreateNewList}>Создать</button>
        </div>
    ) : null}
    
    
    

    {/* Блок #2: Список ключевых слов (Textarea или свернутый вид). */}
{activeBasicListName && (
    isBasicKeywordsVisible ? (
        // Контейнер для развернутого вида
        <div className="expanded-list-container">
            {/* Панель с кнопками для управления содержимым */}
            <div className="list-content-actions">
                <button 
                    className="btn-icon-small" 
                    title="Копировать слова"
                    onClick={handleCopyKeywords}
                    disabled={getActiveBasicKeywords().length === 0}
                >
                    <CopyIcon />
                </button>
                <div className="download-menu-container" ref={downloadMenuRef}>
                    <button 
                        className="btn-icon-small" 
                        title="Экспорт/Скачать список" 
                        onClick={() => setDownloadMenuVisible(p => !p)} 
                        disabled={getActiveBasicKeywords().length === 0}
                    >
                        <DownloadIcon />
                    </button>
                    {isDownloadMenuVisible && (
                        <ul className="download-dropdown-menu">
                            <li onClick={() => { handleDownloadXLSX(); setDownloadMenuVisible(false); }}>Excel</li>
                            <li onClick={() => { handleDownloadCSV(); setDownloadMenuVisible(false); }}>CSV</li>
                        </ul>
                    )}
                </div>
            </div>
            
            {/* Сам список слов */}
            <textarea 
                value={getActiveBasicKeywords().join('\n')} 
                onChange={(e) => updateActiveBasicKeywords(e.target.value.split('\n'))}
                rows={8} 
                placeholder="Слова в активном списке..." 
                className="basic-keywords-textarea"
            />
        </div>
    ) : (
        // Свернутый вид (остается без изменений)
        <div className="collapsed-source-phrases" onClick={() => setIsBasicKeywordsVisible(true)}>
            <span>Слов в списке "{activeBasicListName}": <strong>{activeKeywordsCount}</strong></span>
            <ChevronDownIcon /> 
        </div>
    )
)}
    
    
            </div>
            
            <div className="caption">Simple Negative Keywords Tool (Cloud Version)</div>
            {message && <div id="snackbar" className="show">{message}</div>}
            
            

            {highlightMatchesModal && (
            <div className="modal-overlay">
                {/* Добавляем ref к этому div */}
             <div className="modal" ref={matchesModalRef}>
            {/* Добавляем новую кнопку-крестик */}
            <button className="modal-close-btn" onClick={() => setHighlightMatchesModal(false)} title="Закрыть">
                <CloseIcon />
            </button>
            <h3>Совпадения для "{wordToHighlight}"</h3>
            
            




            <ul className="matches-list scrollable">
    {matchesList.length > 0 ? (
        matchesList.map((match, index) => (
            <li key={index} className="match-item">
                <span>{match}</span>
                <button
                    className="btn-remove-match"
                    title="Убрать фразу"
                    onClick={() => handleRemoveMatch(index)}
                >
                    <CloseIcon />
                </button>
            </li>
        ))
    ) : ( <p>Совпадений не найдено.</p> )}
</ul>
            
            
            <div className="modal-buttons">
                <button onClick={handleCopyMatchesList} disabled={matchesList.length === 0}>Копировать</button>
                {/* Старая кнопка "Закрыть" была здесь, мы ее убрали */}
                     </div>
                  </div>
               </div>
            )}
            
            
            
{showListSelectionModal && (
    <div className="modal-overlay">
        <div className="modal" ref={listSelectionModalRef}>
            <button className="modal-close-btn" onClick={() => setShowListSelectionModal(false)} title="Закрыть">
                <CloseIcon />
            </button>
            <h3>Выбор списка</h3>
            <input 
                type="text" 
                value={basicListSearchQuery} 
                onChange={(e) => setBasicListSearchQuery(e.target.value)} 
                placeholder="Найти список..." 
                className="basic-list-search-input" 
                autoComplete="off" 
                autoFocus
            />
            <ul className="search-results-list scrollable">
                {filteredBasicLists.length > 0 ? (
                    filteredBasicLists.map(list => (<li key={list.name} className={`search-results-item ${list.name === activeBasicListName ? 'active' : ''}`} onClick={() => handleSelectSearchedList(list.name)}>{list.name} ({list.keywords.length})</li>))
                ) : ( <li className="search-results-item no-results">Нет совпадений.</li> )}
            </ul>
            <div className="modal-buttons">
                {/* Кнопка "Закрыть" была здесь, теперь её нет */}
            </div>
        </div>
    </div>
)}


        </div>
    );
}

export default DashboardPage;